import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessagesSquare, Search, Send, Paperclip, Loader2, CheckCheck, FileText,
  CircleDot, X, Plane, Tag, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiErrorMessage } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { fmtChatTime } from "@/pages/chat/ChatPage";

const SCOPE_LABEL = {
  presale: "Pre-venta · conversaciones con clientes antes de vender.",
  support: "Support · viajes ya vendidos (gestión del viaje).",
  mine: "Míos · conversaciones asignadas a mí.",
  closed: "Cerrados.",
  all: "Todas las conversaciones de clientes.",
};



function useIsMobile() {
  const query = "(max-width: 767px)";
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMobile(e.matches);
    mq.addEventListener("change", handler);
    setMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return mobile;
}

const StageBadge = ({ stage }) =>
  stage === "support" ? (
    <Badge variant="outline" className="border-sky-200 bg-sky-50 text-[9px] text-sky-700"><Tag className="mr-1 h-2.5 w-2.5" />Support</Badge>
  ) : (
    <Badge variant="outline" className="border-orange-200 bg-orange-50 text-[9px] text-orange-700"><Tag className="mr-1 h-2.5 w-2.5" />Pre-venta</Badge>
  );

// ============================= Hilo =============================
function ConversationThread({ conversationId }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["cc-conv", conversationId],
    queryFn: async () => (await api.get(`/client-chat/conversations/${conversationId}`)).data,
    refetchInterval: 4000,
    enabled: !!conversationId,
  });
  const conv = data?.conversation;
  const messages = data?.messages || [];
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const sendMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      return (await api.post(`/client-chat/conversations/${conversationId}/reply`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { setText(""); setFile(null); qc.invalidateQueries({ queryKey: ["cc-conv", conversationId] }); qc.invalidateQueries({ queryKey: ["cc-list"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const statusMut = useMutation({
    mutationFn: async (status) => (await api.patch(`/client-chat/conversations/${conversationId}`, { status })).data,
    onSuccess: (d) => { toast.success(d.status === "closed" ? "Conversación cerrada" : "Reabierta"); qc.invalidateQueries({ queryKey: ["cc-conv", conversationId] }); qc.invalidateQueries({ queryKey: ["cc-list"] }); },
  });

  const viewAttachment = async (m) => {
    try {
      const res = await api.get(`/client-chat/conversations/${conversationId}/messages/${m.id}/attachment`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  if (isLoading) return <Skeleton className="h-full min-h-[420px] w-full" />;
  if (!conv) return null;
  const closed = conv.status === "closed";

  return (
    <div className="flex h-full flex-col" data-testid="cc-thread">
      <div className="flex items-start justify-between gap-3 border-b p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-heading text-base font-semibold">{conv.client_name}</h3>
            <StageBadge stage={conv.stage} />
            {closed && <Badge variant="outline" className="border-slate-200 bg-slate-50 text-[9px] text-slate-600">Cerrada</Badge>}
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <Plane className="h-3 w-3" />{conv.trip_title || "—"}{conv.assignee_name ? ` · ${conv.assignee_name}` : ""}
          </p>
        </div>
        <Button size="sm" variant={closed ? "secondary" : "outline"} onClick={() => statusMut.mutate(closed ? "open" : "closed")} disabled={statusMut.isPending} data-testid="cc-status-toggle">
          {closed ? <><RotateCcw className="mr-1.5 h-4 w-4" />Reabrir</> : <><CheckCheck className="mr-1.5 h-4 w-4" />Cerrar</>}
        </Button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto bg-muted/20 p-4" data-testid="cc-messages">
        {messages.map((m) => {
          if (m.sender === "system") {
            return (
              <div key={m.id} className="mx-auto max-w-[85%] rounded-lg border bg-amber-50 px-3 py-2 text-center text-[11px] text-amber-800" data-testid="cc-system-msg">
                {m.text}
              </div>
            );
          }
          const staff = m.sender === "staff";
          return (
            <div key={m.id} className={`flex gap-2 ${staff ? "flex-row-reverse" : ""}`}>
              <UserAvatar name={m.author_name} color={m.author_color || (staff ? "slate" : "terra")} size="md" />
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${staff ? "rounded-tr-sm bg-primary text-primary-foreground" : "rounded-tl-sm border bg-card"}`}>
                <p className="mb-0.5 text-[11px] font-semibold opacity-80">{staff ? m.author_name : `${m.author_name} (cliente)`}</p>
                {m.text && <p className="whitespace-pre-wrap break-words text-sm">{m.text}</p>}
                {m.attachment && (
                  <button onClick={() => viewAttachment(m)} className={`mt-1.5 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs ${staff ? "bg-primary-foreground/15 hover:bg-primary-foreground/25" : "bg-muted hover:bg-muted/70"}`} data-testid={`cc-attach-${m.id}`}>
                    <FileText className="h-3.5 w-3.5" /><span className="truncate">{m.attachment.filename}</span>
                  </button>
                )}
                <p className={`mt-1 text-right text-[10px] ${staff ? "opacity-70" : "text-muted-foreground"}`}>{fmtChatTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="border-t p-3">
        {file && <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground"><Paperclip className="h-3.5 w-3.5" /><span className="truncate">{file.name}</span><button onClick={() => setFile(null)}><X className="h-3.5 w-3.5" /></button></div>}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cc-file-input" />
          <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => fileRef.current?.click()} title="Adjuntar"><Paperclip className="h-4 w-4" /></Button>
          <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (text.trim() || file) sendMut.mutate(); } }}
            placeholder="Responder al cliente…  (Enter para enviar)" className="min-h-[40px] resize-none" data-testid="cc-msg-input" />
          <Button size="icon" className="h-9 w-9 shrink-0" onClick={() => sendMut.mutate()} disabled={(!text.trim() && !file) || sendMut.isPending} data-testid="cc-msg-send">
            {sendMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================= Lista =============================
function ConversationList({ stage, status, q, selectedId, onSelect }) {
  const { data, isLoading } = useQuery({
    queryKey: ["cc-list", stage, status, q],
    queryFn: async () => (await api.get("/client-chat/conversations", { params: { stage, status, q: q || undefined } })).data,
    refetchInterval: 5000,
  });
  const items = data?.items || [];
  if (isLoading) return <div className="space-y-2 p-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  if (items.length === 0) return <p className="p-6 text-center text-sm text-muted-foreground" data-testid="cc-list-empty">No hay conversaciones.</p>;
  return (
    <div className="divide-y" data-testid="cc-list">
      {items.map((c) => (
        <button key={c.id} onClick={() => onSelect(c.id)}
          className={`flex w-full flex-col gap-1 px-3.5 py-3 text-left transition-colors ${selectedId === c.id ? "bg-accent" : "hover:bg-muted/50"}`}
          data-testid={`cc-item-${c.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 truncate text-sm font-medium">
              {c.unread_for_staff && <CircleDot className="h-3 w-3 shrink-0 text-primary" data-testid={`cc-unread-${c.id}`} />}
              {c.client_name}
            </span>
            <span className="shrink-0 text-[10px] text-muted-foreground">{fmtChatTime(c.last_message_at)}</span>
          </div>
          <span className="truncate text-[11px] text-muted-foreground">{c.trip_title || "—"}</span>
          {c.last_message_preview && <span className={`truncate text-xs ${c.unread_for_staff ? "font-medium text-foreground" : "text-muted-foreground"}`}>{c.last_message_by ? `${String(c.last_message_by).split(" ")[0]}: ` : ""}{c.last_message_preview}</span>}
          <div className="flex items-center gap-1.5"><StageBadge stage={c.stage} />{c.status === "closed" && <Badge variant="outline" className="text-[9px]">Cerrada</Badge>}</div>
        </button>
      ))}
    </div>
  );
}

// ============================= Página =============================
export default function ClientChatPage() {
  const { moduleId } = useParams();
  const isSupport = moduleId === "chat_support";
  const stage = isSupport ? "support" : "presale";
  const [status, setStatus] = useState("open");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const isMobile = useIsMobile();

  const { data: unreadData } = useQuery({
    queryKey: ["cc-unread"],
    queryFn: async () => (await api.get("/client-chat/unread")).data,
    refetchInterval: 15000,
  });
  const openUnread = isSupport ? (unreadData?.support || 0) : (unreadData?.presale || 0);

  return (
    <div className="space-y-4" data-testid="cc-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><MessagesSquare className="h-6 w-6 text-primary" />{isSupport ? "Chat Support" : "Chat Clientes"}</h1>
        <p className="text-sm text-muted-foreground">{isSupport ? "Support · viajes ya vendidos (gestión del viaje)." : "Pre-venta · conversaciones con clientes antes de vender."}</p>
      </div>

      <Card className="overflow-hidden">
        <div className="grid h-[calc(100vh-15rem)] min-h-[480px] grid-cols-1 md:grid-cols-[340px_1fr]">
          <div className="flex flex-col border-r">
            <div className="border-b p-2">
              <Tabs value={status} onValueChange={(v) => { setStatus(v); setSelectedId(null); }}>
                <TabsList className="grid h-8 w-full grid-cols-2" data-testid="cc-tabs">
                  <TabsTrigger value="open" className="text-xs" data-testid="cc-tab-open">
                    Abiertos
                    {openUnread > 0 && <span className="ml-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground" data-testid="cc-tab-open-badge">{openUnread > 9 ? "9+" : openUnread}</span>}
                  </TabsTrigger>
                  <TabsTrigger value="closed" className="text-xs" data-testid="cc-tab-closed">Cerrados</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="border-b p-2.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente…" className="h-9 pl-8" data-testid="cc-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ConversationList stage={stage} status={status} q={q} selectedId={selectedId} onSelect={setSelectedId} />
            </div>
          </div>
          <div className="hidden md:block">
            {selectedId ? <ConversationThread conversationId={selectedId} /> : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground" data-testid="cc-empty">
                <MessagesSquare className="h-10 w-10 opacity-30" />
                <p className="text-sm">Elige una conversación de cliente.</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {selectedId && isMobile && (
        <Dialog open onOpenChange={(o) => !o && setSelectedId(null)}>
          <DialogContent className="block h-[85vh] max-w-2xl p-0">
            <DialogHeader className="sr-only"><DialogTitle>Conversación</DialogTitle></DialogHeader>
            <ConversationThread conversationId={selectedId} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
